/**
 * Macro arithmetic. Every nutrient in the catalog is stored per 100 g; every
 * logged entry stores absolute totals frozen at log time. These helpers are the
 * only place that conversion happens, so the client and the API can never drift.
 */

/** Nutrients as stored on a food/recipe/component row: per 100 g. */
export interface Per100g {
  kcal: number
  protein: number
  carbs: number
  fat: number
  sugars?: number | null
  satFat?: number | null
  fiber?: number | null
  salt?: number | null
}

/** Absolute nutrient totals for a concrete quantity. */
export interface MacroTotals {
  kcal: number
  protein: number
  carbs: number
  fat: number
  sugars: number | null
  satFat: number | null
  fiber: number | null
  salt: number | null
}

const NULLABLE_KEYS = ['sugars', 'satFat', 'fiber', 'salt'] as const

/** Round to `dp` decimals without float dust (0.1 + 0.2 style drift). */
export function round(value: number, dp = 2): number {
  const f = 10 ** dp
  return Math.round((value + Number.EPSILON) * f) / f
}

/** Scale per-100 g nutrients to the totals for `grams`. */
export function scaleToGrams(per100g: Per100g, grams: number): MacroTotals {
  const f = grams / 100
  const totals: MacroTotals = {
    kcal: round(per100g.kcal * f),
    protein: round(per100g.protein * f),
    carbs: round(per100g.carbs * f),
    fat: round(per100g.fat * f),
    sugars: null,
    satFat: null,
    fiber: null,
    salt: null,
  }
  for (const key of NULLABLE_KEYS) {
    const v = per100g[key]
    totals[key] = v == null ? null : round(v * f, key === 'salt' ? 3 : 2)
  }
  return totals
}

/** Sum totals, treating null as "unknown" — a null anywhere makes the sum null. */
export function sumTotals(entries: readonly MacroTotals[]): MacroTotals {
  const out: MacroTotals = {
    kcal: 0,
    protein: 0,
    carbs: 0,
    fat: 0,
    sugars: 0,
    satFat: 0,
    fiber: 0,
    salt: 0,
  }
  for (const key of NULLABLE_KEYS) {
    if (entries.some((e) => e[key] == null)) out[key] = null
  }
  for (const e of entries) {
    out.kcal += e.kcal
    out.protein += e.protein
    out.carbs += e.carbs
    out.fat += e.fat
    for (const key of NULLABLE_KEYS) {
      const acc = out[key]
      const v = e[key]
      if (acc != null && v != null) out[key] = acc + v
    }
  }
  out.kcal = round(out.kcal)
  out.protein = round(out.protein)
  out.carbs = round(out.carbs)
  out.fat = round(out.fat)
  for (const key of NULLABLE_KEYS) {
    const acc = out[key]
    if (acc != null) out[key] = round(acc, key === 'salt' ? 3 : 2)
  }
  return out
}

/** The four nutrients a label may simply not state. */
export type NullableNutrient = (typeof NULLABLE_KEYS)[number]

/**
 * Sum a day, counting every figure that exists.
 *
 * `sumTotals` treats a missing nutrient as unknown and nulls the whole sum,
 * which is right for one dish — a cake whose butter never declared its fibre has
 * an unknown fibre content, not a smaller one. It is wrong for a diary. Almost
 * no retailer publishes fibre, so a single scanned packet blanked the fibre,
 * sugar, saturated-fat and salt meters for the entire day, every day, and four
 * meters that are permanently empty read as four features that do not work
 * rather than as an honest absence.
 *
 * So a day counts what it knows. The figure is a floor, never an overstatement,
 * and `undeclaredNutrients` names the ones that some row never stated so the
 * screen can say which numbers are short rather than quietly presenting a floor
 * as a total.
 */
export function sumKnown(entries: readonly MacroTotals[]): MacroTotals {
  const out: MacroTotals = {
    kcal: 0,
    protein: 0,
    carbs: 0,
    fat: 0,
    sugars: 0,
    satFat: 0,
    fiber: 0,
    salt: 0,
  }
  for (const e of entries) {
    out.kcal += e.kcal
    out.protein += e.protein
    out.carbs += e.carbs
    out.fat += e.fat
    for (const key of NULLABLE_KEYS) {
      const v = e[key]
      if (v != null) out[key] = (out[key] ?? 0) + v
    }
  }
  out.kcal = round(out.kcal)
  out.protein = round(out.protein)
  out.carbs = round(out.carbs)
  out.fat = round(out.fat)
  for (const key of NULLABLE_KEYS) {
    const acc = out[key]
    if (acc != null) out[key] = round(acc, key === 'salt' ? 3 : 2)
  }
  return out
}

/** The nutrients at least one of these rows never declared. */
export function undeclaredNutrients(entries: readonly MacroTotals[]): NullableNutrient[] {
  return NULLABLE_KEYS.filter((key) => entries.some((e) => e[key] == null))
}

/** Convert absolute totals back to a per-100 g profile for `grams` of product. */
export function totalsToPer100g(totals: MacroTotals, grams: number): Per100g {
  if (grams <= 0) throw new RangeError('grams must be > 0')
  const f = 100 / grams
  return {
    kcal: round(totals.kcal * f),
    protein: round(totals.protein * f),
    carbs: round(totals.carbs * f),
    fat: round(totals.fat * f),
    sugars: totals.sugars == null ? null : round(totals.sugars * f),
    satFat: totals.satFat == null ? null : round(totals.satFat * f),
    fiber: totals.fiber == null ? null : round(totals.fiber * f),
    salt: totals.salt == null ? null : round(totals.salt * f, 3),
  }
}

export interface RecipeIngredientInput {
  per100g: Per100g
  rawWeightG: number
}

/**
 * Cooked-weight ("water loss") math: nutrients come from the RAW ingredients,
 * but the user weighs and logs the COOKED batch, so the per-100 g profile is
 * divided by the cooked weight. A stew that loses water gets denser per gram;
 * pasta that absorbs water gets lighter.
 */
export function recipePer100gCooked(
  ingredients: readonly RecipeIngredientInput[],
  cookedWeightG: number,
): Per100g {
  if (cookedWeightG <= 0) throw new RangeError('cookedWeightG must be > 0')
  // sumKnown: one ingredient whose label never mentioned fibre must not make the
  // whole dish's fibre unknown. It made the dish's four secondary nutrients null
  // in almost every case — flour and oil rarely state them — and a diary that
  // then logs a portion of that dish inherited the nulls and blanked the day's
  // fibre, sugar, saturated fat and salt entirely. The figure is a floor, which
  // is what a sum of partial labels can honestly be.
  const totals = sumKnown(ingredients.map((i) => scaleToGrams(i.per100g, i.rawWeightG)))
  return totalsToPer100g(totals, cookedWeightG)
}

/** Total raw input weight — shown next to cooked weight so the yield is visible. */
export function rawWeightTotal(ingredients: readonly RecipeIngredientInput[]): number {
  return round(
    ingredients.reduce((sum, i) => sum + i.rawWeightG, 0),
    2,
  )
}

/**
 * Yield ratio: cooked / raw. < 1 means water was lost (roasting, frying),
 * > 1 means water was absorbed (rice, pasta, oats).
 */
export function yieldRatio(
  ingredients: readonly RecipeIngredientInput[],
  cookedWeightG: number,
): number {
  const raw = rawWeightTotal(ingredients)
  if (raw <= 0) throw new RangeError('raw weight must be > 0')
  return round(cookedWeightG / raw, 4)
}

export interface FastFoodComponentInput {
  per100g: Per100g
  /** Weight of one unit of this component (one bun, one sauce portion). */
  unitWeightG: number
  /** How many units are in the build. */
  quantity: number
}

/** Sum a fast-food build's components into totals plus its assembled weight. */
export function buildTotals(components: readonly FastFoodComponentInput[]): {
  totals: MacroTotals
  weightG: number
} {
  const weightG = round(
    components.reduce((sum, c) => sum + c.unitWeightG * c.quantity, 0),
    2,
  )
  // Same reason as recipePer100gCooked: a bun that never declared its fibre must
  // not erase the salt the sauce did declare.
  const totals = sumKnown(
    components.map((c) => scaleToGrams(c.per100g, c.unitWeightG * c.quantity)),
  )
  return { totals, weightG }
}

/**
 * Atwater energy estimate — used to sanity-check OCR and scraped values.
 *
 * Fibre counts at 2 kcal/g because that is how the label in front of the user
 * was computed (Regulation 1169/2011), and because EU carbohydrate figures
 * exclude it. Leaving it out flagged every high-fibre product as a misread: a
 * psyllium husk declaring a perfectly correct 200 kcal derived to 18.
 */
export function atwaterKcal(
  per100g: Pick<Per100g, 'protein' | 'carbs' | 'fat'> & { fiber?: number | null },
): number {
  const fiber = per100g.fiber ?? 0
  return round(per100g.protein * 4 + per100g.carbs * 4 + per100g.fat * 9 + fiber * 2, 1)
}

export const KJ_PER_KCAL = 4.184

export function kjToKcal(kj: number): number {
  return round(kj / KJ_PER_KCAL, 1)
}

export function kcalToKj(kcal: number): number {
  return round(kcal * KJ_PER_KCAL, 1)
}

export type Sex = 'male' | 'female'

export interface BmrInput {
  weightKg: number
  heightCm: number
  ageYears: number
  sex: Sex
}

/**
 * Mifflin-St Jeor basal metabolic rate. Needed because no wearable path exposes
 * resting calories — Huawei publishes active calories only — so total daily
 * expenditure has to be assembled as BMR + active burn.
 */
export function bmrMifflinStJeor({ weightKg, heightCm, ageYears, sex }: BmrInput): number {
  const base = 10 * weightKg + 6.25 * heightCm - 5 * ageYears
  return Math.round(sex === 'male' ? base + 5 : base - 161)
}

export interface DayEnergyInput {
  kcalTarget: number
  loggedKcal: number
  activeKcal?: number | null
}

export interface DayEnergy {
  kcalTarget: number
  loggedKcal: number
  activeKcal: number
  /** What is still available to eat today. Negative means over target. */
  remainingKcal: number
  /** 0..1 for the progress ring; clamped so the ring never overflows. */
  ringProgress: number
  isOver: boolean
}

/** The Today-screen ring: target + active burn − logged. */
export function dayEnergy({ kcalTarget, loggedKcal, activeKcal }: DayEnergyInput): DayEnergy {
  const active = activeKcal ?? 0
  const budget = kcalTarget + active
  const remaining = round(budget - loggedKcal, 0)
  return {
    kcalTarget,
    loggedKcal: round(loggedKcal, 0),
    activeKcal: round(active, 0),
    remainingKcal: remaining,
    ringProgress: budget > 0 ? Math.min(1, Math.max(0, loggedKcal / budget)) : 0,
    isOver: remaining < 0,
  }
}
